import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { HttpService } from '@services/http.service';
import { EndpointParams } from '@models/utils/endpoint-params';
import { SepaDebit } from '@models/integration-method/debit-sepa/debit-sepa';
import { IntegrationMethodDefault } from '@models/integration-method/integration-method-default';

@Injectable({
    providedIn: 'root'
})
export class SepaService extends HttpService {
    private readonly defaultEndpointParams: EndpointParams = { app: 'payments', version: 1 };

    private readonly sepaSubject = new BehaviorSubject<SepaDebit[]>([]);
    private readonly defaultIdSubject = new BehaviorSubject<string>('');
    private readonly loadingSubject = new BehaviorSubject<boolean>(false);

    private readonly dataStore: {
        sepaDebits: SepaDebit[];
        defaultId: string;
        actualDefaultId: string;
    } = {
            sepaDebits: [],
            defaultId: '',
            actualDefaultId: ''
        };

    readonly sepaDebits: Observable<SepaDebit[]> = this.sepaSubject.asObservable();
    readonly default: Observable<string> = this.defaultIdSubject.asObservable();
    readonly loading: Observable<boolean> = this.loadingSubject.asObservable();

    listSepaDebits(): Observable<SepaDebit[]> {
        return this.get('debits/sepa/member', this.defaultEndpointParams);
    }

    list(): void {
        this.setLoading(true);
        this.listSepaDebits().subscribe(items => {
            this.dataStore.sepaDebits = items;
            const def = this.dataStore.sepaDebits.find(c => c.defaultPaymentMethod)?.id ?? '';
            this.dataStore.defaultId = def;
            this.dataStore.actualDefaultId = def;
            this.next();
            this.nextDefault();
            this.setLoading(false);
        });
    }

    getDefaultSepaId(): Observable<string> {
        return this.sepaDebits.pipe(map(list => list.find(c => c.defaultPaymentMethod)?.id ?? ''));
    }

    cancelDefault(): void {
        this.dataStore.defaultId = this.dataStore.actualDefaultId;
        this.nextDefault();
    }

    setDefault(integrationMethodDefault: IntegrationMethodDefault): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            this.put('member/paymentmethod/default', this.defaultEndpointParams, integrationMethodDefault).subscribe({
                next: () => {
                    this.dataStore.actualDefaultId = integrationMethodDefault.idPaymentMethod;
                    this.dataStore.defaultId = integrationMethodDefault.idPaymentMethod;
                    this.nextDefault();
                    observable.next(true);
                },
                error: () => {
                    this.cancelDefault();
                    observable.next(false);
                }
            });
        });
    }

    removeSepa(idSepaDebit: string): Observable<boolean> {
        this.setLoading(true);
        return new Observable<boolean>((observable) => {
            this.delete(`debits/sepa/member/${idSepaDebit}`, this.defaultEndpointParams).subscribe({
                next: () => {
                    observable.next(true);
                    this.list();
                },
                error: () => {
                    observable.next(false);
                }
            });
        });
    }

    removeSepaCheck(idSepaDebit: string): Observable<boolean> {
        return new Observable<boolean>((observable) => {
            this.delete(`debits/sepa/member/${idSepaDebit}/check`, this.defaultEndpointParams, this.noShowErrorNotification).subscribe({
                next: () => {
                    observable.next(true);
                },
                error: (error) => {
                    observable.error(error);
                }
            });
        });
    }

    private next(): void {
        this.sepaSubject.next([...this.dataStore.sepaDebits]);
    }

    private nextDefault(): void {
        this.defaultIdSubject.next(this.dataStore.defaultId);
    }

    private setLoading(value: boolean): void {
        this.loadingSubject.next(value);
    }
}
